import {
  Heading,
  Text,
  HStack,
  VStack,
  Badge,
  Button,
  useColorModeValue,
  Box,
  Image,
  SimpleGrid,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Icon,
} from '@chakra-ui/react';
import { useState } from 'react';
import { CRMIdea } from '../types';
import { format } from 'date-fns';
import { Timestamp } from 'firebase/firestore';
import { Image as ImageIcon } from 'lucide-react';

interface CRMIdeaCardProps {
  idea: CRMIdea;
  onComplete: () => void;
  onDelete: () => void;
  canComplete: boolean;
  canDelete: boolean;
}

/**
 * CRM Idea Card Component
 * Displays a single idea or fix with screenshot support
 */
export function CRMIdeaCard({
  idea,
  onComplete,
  onDelete,
  canComplete,
  canDelete,
}: CRMIdeaCardProps) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const headingColor = useColorModeValue('gray.900', 'white');
  const textColor = useColorModeValue('gray.600', 'gray.400');
  const metaColor = useColorModeValue('gray.500', 'gray.500');

  const formatDate = (date: Date | string | Timestamp | undefined) => {
    if (!date) return 'N/A';
    if (date instanceof Date) {
      return format(date, 'MMM d, yyyy h:mm a');
    }
    if (date instanceof Timestamp) {
      return format(date.toDate(), 'MMM d, yyyy h:mm a');
    }
    return format(new Date(date), 'MMM d, yyyy h:mm a');
  };

  const handleImageClick = (url: string) => {
    setSelectedImage(url);
    onOpen();
  };

  return (
    <>
      <Box
        bg={cardBg}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="lg"
        p={5}
        shadow="sm"
        transition="all 0.2s"
        _hover={{ shadow: 'md' }}
      >
        <VStack align="stretch" spacing={3}>
          {/* Header with badges */}
          <HStack justify="space-between">
            <HStack spacing={2}>
              <Badge colorScheme={idea.type === 'idea' ? 'blue' : 'orange'}>
                {idea.type === 'idea' ? '💡 Idea' : '🔧 Fix'}
              </Badge>
              <Badge colorScheme={idea.status === 'completed' ? 'green' : 'gray'}>
                {idea.status === 'completed' ? '✓ Completed' : '🆕 New'}
              </Badge>
            </HStack>
            {idea.screenshots && idea.screenshots.length > 0 && (
              <HStack spacing={1}>
                <Icon as={ImageIcon} boxSize={3} color="gray.400" />
                <Text fontSize="xs" color="gray.400">
                  {idea.screenshots.length}
                </Text>
              </HStack>
            )}
          </HStack>
          
          {/* Title */}
          <Heading size="md" color={headingColor} noOfLines={2}>
            {idea.title}
          </Heading>
          
          {/* Description */}
          <Text color={textColor} fontSize="sm" noOfLines={4}>
            {idea.description}
          </Text>
          
          {/* Screenshot Thumbnails */}
          {idea.screenshots && idea.screenshots.length > 0 && (
            <Box>
              <Text fontSize="xs" fontWeight="medium" color={metaColor} mb={2}>
                Screenshots:
              </Text>
              <SimpleGrid columns={Math.min(idea.screenshots.length, 4)} spacing={2}>
                {idea.screenshots.slice(0, 4).map((screenshot, index) => (
                  <Image
                    key={index}
                    src={screenshot.url}
                    alt={screenshot.name}
                    boxSize="50px"
                    objectFit="cover"
                    borderRadius="md"
                    cursor="pointer"
                    border="1px solid"
                    borderColor={borderColor}
                    onClick={() => handleImageClick(screenshot.url)}
                    _hover={{ transform: 'scale(1.05)', shadow: 'md' }}
                    transition="all 0.2s"
                  />
                ))}
                {idea.screenshots.length > 4 && (
                  <Box
                    boxSize="50px"
                    bg="gray.100"
                    borderRadius="md"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    cursor="pointer"
                    onClick={() => handleImageClick(idea.screenshots![4].url)}
                  >
                    <Text fontSize="xs" fontWeight="bold" color="gray.600">
                      +{idea.screenshots.length - 4}
                    </Text>
                  </Box>
                )}
              </SimpleGrid>
            </Box>
          )}
          
          {/* Metadata */}
          <Box fontSize="xs" color={metaColor} pt={2} borderTop="1px solid" borderColor={borderColor}>
            <Text>
              <strong>From:</strong> {idea.createdByName}
            </Text>
            <Text>
              <strong>Submitted:</strong> {formatDate(idea.createdAt)}
            </Text>
            {idea.status === 'completed' && idea.completedByName && (
              <>
                <Text>
                  <strong>Completed by:</strong> {idea.completedByName}
                </Text>
                <Text>
                  <strong>Completed:</strong> {formatDate(idea.completedAt)}
                </Text>
              </>
            )}
          </Box>
          
          {/* Actions */}
          <HStack spacing={2} pt={2}>
            {idea.status === 'new' && canComplete && (
              <Button size="sm" colorScheme="green" onClick={onComplete}>
                Mark Complete
              </Button>
            )}
            {canDelete && (
              <Button size="sm" colorScheme="red" variant="ghost" onClick={onDelete}>
                Delete
              </Button>
            )}
          </HStack>
        </VStack>
      </Box>

      {/* Image Preview Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="4xl" isCentered>
        <ModalOverlay backdropFilter="blur(10px)" />
        <ModalContent bg="transparent" boxShadow="none">
          <ModalCloseButton color="white" bg="blackAlpha.600" borderRadius="full" />
          <ModalBody p={0} display="flex" justifyContent="center" alignItems="center">
            {selectedImage && (
              <Image
                src={selectedImage}
                alt="Screenshot preview"
                maxH="80vh"
                maxW="90vw"
                objectFit="contain"
                borderRadius="lg"
              />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}

export default CRMIdeaCard;



